import { useEffect, useMemo, useState } from "react"
import type { CSSProperties } from "react"
import { Panel } from "../components/ui"
import { fmtCompact, fmtInt, toNum } from "../game/format"
import { useBakeryStore } from "../game/bakeryStore"
import { glowShadow, ringStyle } from "../game/cosmetics"
import { avatarCdnUrl } from "@cookies/shared/cdn"
import type { LeaderboardEntry } from "../game/types"
import { apiFetch } from "../auth/api"
import { getToken } from "../auth/session"
import { API_TIMEOUT_MS } from "@cookies/shared/config"

function compareEntries(a: LeaderboardEntry, b: LeaderboardEntry): number {
  const ac = toNum(a.contribution)
  const bc = toNum(b.contribution)
  if (bc !== ac) return bc - ac
  const at = toNum(a.totalClicks)
  const bt = toNum(b.totalClicks)
  if (bt !== at) return bt - at
  return a.userId < b.userId ? -1 : a.userId > b.userId ? 1 : 0
}

function bonusTitle(e: LeaderboardEntry): string {
  return `${e.name}: ${fmtInt(e.contribution)} total = clicks x (power ${fmtCompact(e.clickPower ?? "1")} x5 x Rush x2 x Golden x5 x combo to +50% x crit x2) + shared bakery gains. CPS +${fmtCompact(e.cps ?? "0")}/s from modules + automation + projects.`
}

type BoardResponse = { leaderboard: LeaderboardEntry[]; serverTime: number }

export default function LeaderboardTab() {
  const store = useBakeryStore()
  const snap = store.snapshot
  const [manual, setManual] = useState<LeaderboardEntry[] | null>(null)
  const [manualSeq, setManualSeq] = useState(0)
  const [refreshing, setRefreshing] = useState(false)
  const [updatedAt, setUpdatedAt] = useState<number | null>(null)
  const [refreshError, setRefreshError] = useState<string | null>(null)
  const guildId = snap?.guild?.id ?? ""
  const liveSeq = snap?.seq ?? 0
  useEffect(() => {
    setManual(null)
    setManualSeq(0)
    setUpdatedAt(null)
    setRefreshError(null)
  }, [guildId])
  const rows = useMemo(() => {
    const fresh = manual !== null && manualSeq >= liveSeq ? manual : (snap?.leaderboard ?? [])
    const list = [...fresh]
    list.sort(compareEntries)
    return list
  }, [manual, manualSeq, liveSeq, snap])
  const online = rows.filter((r) => r.active).length
  const total = rows.reduce((acc, r) => acc + toNum(r.contribution), 0)
  async function onRefresh(): Promise<void> {
    if (guildId === "" || refreshing) return
    const token = getToken()
    if (token === null || token === "") {
      setRefreshError("Sign in again to refresh.")
      return
    }
    setRefreshing(true)
    setRefreshError(null)
    try {
      const data = await apiFetch<BoardResponse>("/api/bakery/" + encodeURIComponent(guildId) + "/leaderboard", { token, timeoutMs: API_TIMEOUT_MS })
      setManual([...data.leaderboard])
      setManualSeq(snap?.seq ?? 0)
      setUpdatedAt(data.serverTime ?? Date.now())
    } catch {
      setRefreshError("Refresh failed — try again.")
      return
    } finally {
      setRefreshing(false)
    }
  }
  if (snap === null) {
    return <div className="cb-panel center small" role="status">Loading…</div>
  }
  if (rows.length === 0) {
    return (
      <div className="cb-panel center small" role="status">
        No bakers yet. Click the cookie to join the leaderboard.
      </div>
    )
  }
  return (
    <div>
      <Panel className="center">
        <div style={{ display: "flex", gap: 8, alignItems: "center", justifyContent: "center", flexWrap: "wrap" }}>
          <p className="dim small" style={{ margin: 0 }} title="Live totals include global bonuses: click power x5, Rush x2, Golden x5, combo to +50%, crit x2. CPS from modules + automation + projects.">
            <span aria-label="Live leaderboard">● Live</span> · {rows.length} baker{rows.length === 1 ? "" : "s"} · {online} online · 🍪 {fmtCompact(total)} total
          </p>
          <button type="button" className={refreshing ? "cb-buy off" : "cb-buy"} disabled={refreshing || guildId === ""} onClick={() => void onRefresh()}>
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
        </div>
        {updatedAt !== null && <p className="dim small" style={{ margin: "4px 0 0" }}>Live · Updated {new Date(updatedAt).toLocaleTimeString()}</p>}
        {refreshError !== null && <p className="dim small" role="alert" style={{ margin: "4px 0 0" }}>{refreshError}</p>}
      </Panel>
      <ol style={{ listStyle: "none", margin: "12px 0 0", padding: 0, display: "flex", flexDirection: "column", gap: 8 }}>
        {rows.map((e, i) => {
          const url = avatarCdnUrl(e.userId, e.avatar ?? "", 64)
          const glow = glowShadow(e.cosmetics?.glow)
          const ring = ringStyle(e.cosmetics?.ring)
          return (
            <li key={e.userId} className="cb-card" title={bonusTitle(e)}>
              <span className="dim" style={{ minWidth: 28, textAlign: "center" }}>#{i + 1}</span>
              <span className="cb-card-ico" style={{ overflow: "hidden", padding: 0, ...(ring ?? {}) } as CSSProperties} aria-hidden="true">
                {url !== "" ? (
                  <img src={url} alt="" width={36} height={36} style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 10 }} loading="lazy" />
                ) : (
                  <span>{e.name.slice(0, 1)}</span>
                )}
              </span>
              <div className="cb-card-body">
                <b style={glow ? { textShadow: glow } : undefined}>{e.name} {e.active && <span aria-label="online">🟢</span>}</b>
                <small>Lv {e.level} · {e.specialization ?? e.spec}</small>
                <small className="dim" title={bonusTitle(e)}>🍪 {fmtInt(e.contribution)} total · +{fmtCompact(e.cps ?? "0")}/sec · ⚡ {fmtCompact(e.clickPower ?? "1")}</small>
              </div>
            </li>
          )
        })}
      </ol>
      <p className="dim small" style={{ marginTop: 12 }}>
        Only bakers who baked appear here.
      </p>
    </div>
  )
}
